import { ThemeOptions } from '@mui/material/styles';
import { LabComponentsPropsList } from '@mui/lab/themeAugmentation';
import Config from '../config';

export type _internalThemeOptions = LabComponentsPropsList;

// REACT_APP_COLOR_PRIMARY_LIGHT='#ab5d9a'
// REACT_APP_COLOR_PRIMARY_MAIN='#3a6aff'
// REACT_APP_COLOR_PRIMARY_DARK='#4b0041'

export const DefaultTheme: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: Config.GLOBAL_PRIMARY_COLOR,
        },
        secondary: {
            main: Config.GLOBAL_PRIMARY_COLOR,
        },
        background: {
            default: '#f5f5f5',
            paper: '#fff',
        },
        error: {
            main: '#ff1744',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        htmlFontSize: 14,
        fontSize: 13,
        allVariants: {
            color: '#000000',
        },
        body2: {
            fontWeight: 600,
        },
        body1: {
            '@media (max-width: 400px)': {
                fontSize: '0.8rem',
            },
            fontWeight: 600,
        },
        overline: {
            fontSize: '1rem',
            textTransform: 'unset',
            lineHeight: 'normal',
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                margin: 'dense',
            },
        },
        MuiPaper: {
            defaultProps: {
                square: true,
            },
            styleOverrides: {
                elevation1: {
                    boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.08)',
                    borderRadius: 15,
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                maxWidth: 'xs',
                fullWidth: true,
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: '#edefff',
                    color: '#000000',
                },
                notchedOutline: {
                    border: 'unset',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                outlined: {
                    '&.Mui-focused': {
                        transform: 'translate(4px, -16px) scale(0.9)',
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]': {
                        paddingTop: 8.5,
                        paddingBottom: 8.5,
                    },
                },
                endAdornment: {
                    display: 'none',
                },
                clearIndicator: {
                    display: 'none',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: 'unset',
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                filled: {
                    marginTop: 0,
                },
                root: {
                    marginTop: 0,
                },
                positionStart: {
                    marginTop: 0,
                },
                hiddenLabel: {
                    marginTop: 0,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    color: '#000000',
                    '&.Mui-selected': {
                        color: Config.GLOBAL_PRIMARY_COLOR,
                        backgroundColor: Config.GLOBAL_PRIMARY_COLOR + '24',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: 'inherit',
                    fontWeight: 900,
                    fontSize: '1.1rem',
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 32,
                    color: 'inherit',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                sizeSmall: {
                    width: 28,
                    height: 28,
                    padding: 6,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    justifyContent: 'center',
                },
                root: {
                    flex: 1,
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    color: 'rgb(226, 226, 226)',
                },
                track: {
                    color: Config.GLOBAL_PRIMARY_COLOR,
                    height: 3,
                },
                rail: {
                    height: 3,
                },
                thumb: {
                    color: 'rgb(255,255,255)',
                    border: '3px solid' + Config.GLOBAL_PRIMARY_COLOR,
                    height: 20,
                    width: 20,
                    marginTop: -9,
                },
            },
        },
        MuiMobileStepper: {
            styleOverrides: {
                root: {
                    background: 'transparent',
                },
                dot: {
                    backgroundColor: 'rgb(117, 117, 117)',
                    height: 12,
                    width: 12,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: 14,
                    backgroundColor: '#FFFFFF',
                    color: 'rgb(117,117,117)',
                    borderRadius: 0,
                    padding: '8px 16px',
                },
                arrow: {
                    '&::before': {
                        backgroundColor: '#FFFFFF',
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {},
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: 24,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    alignSelf: 'center',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.9rem',
                    fontWeight: 400,
                    width: 0,
                    flex: '0 1 100%',
                    minWidth: 90,
                    maxWidth: 'fit-content',
                },
                label: {
                    overflow: 'hidden',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    borderTopLeftRadius: 4,
                    borderBottomLeftRadius: 4,
                    borderWidth: 0,
                    padding: '4px 16px',
                    fontSize: '1rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.09)',
                    '&.Mui-selected': {
                        backgroundImage: `linear-gradient(90deg, ${Config.GLOBAL_PRIMARY_COLOR} 0%, #ab5d9a 100%)`,
                        color: `rgb(255, 255, 255)`,
                    },
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                groupedHorizontal: {
                    '&:not(:first-child)': {
                        borderLeft: undefined,
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                    },
                    '&:not(:last-child)': {
                        borderRight: undefined,
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4,
                    },
                },
            },
        },
        MuiTabList: {
            styleOverrides: {
                root: {
                    flex: 1,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minWidth: 'auto',
                    '@media (min-width: 600px)': {
                        minWidth: 'auto',
                    },
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: '1.25rem',
                },
                fontSizeSmall: {
                    fontSize: '1rem',
                },
            },
        },
    },
};
